import type { Locale as AntdLocale } from "ant-design-vue/es/locale-provider"
import en_US from "ant-design-vue/es/locale/en_US"
import zh_CN from "ant-design-vue/es/locale/zh_CN"
import dayjs from "dayjs"
import "dayjs/locale/en"
import "dayjs/locale/zh-cn"
import { defineStore, storeToRefs } from "pinia"
import { container, instanceCachingFactory, registry, singleton } from "tsyringe"
import type { App, Ref } from "vue"
import { createI18n, type Composer, type I18n, type I18nOptions, type Locale, type LocaleMessageDictionary, type VueMessageType } from "vue-i18n"
import { CORE_INITIALIZER, CORE_LOCALE } from "../CoreSymbol"
import type { Initializer } from "./Initializer"
import { injectOptional } from "@/tsyringe-extend"

const AntdMap: { [k: string]: AntdLocale } = {
  zh_CN,
  en_US
}

const DayJSMap: { [k: string]: string } = {
  zh_CN: "zh-cn",
  en_US: "en"
}

/**
 * i18n
 */

export const LocalOptionsInjectKey = "LocalOptionsInjectKey"

export type LocaleMessages = {
  [locale: string]: {
    [key: string]: string | LocaleMessages
  }
}

export type LocalOptions = {
  locale: string
  allowComposition: boolean
  messages: LocaleMessages
  datetimeFormats: Record<string, any>
  numberFormats: Record<string, any>
  legacy: boolean
}

export type LocaleMessage = {
  [locale: string]: LocaleMessageDictionary<VueMessageType>
}

export interface LocaleService {
  setLocale(locale: Locale): void
  currentLocale(): Ref<Locale>
  currentAntdLocaleResource(): Ref<AntdLocale>
  mergeLocaleMessage(message: LocaleMessage): void
}

const i18nDefaultOptions: LocalOptions = {
  locale: "zh_CN",
  allowComposition: true,
  messages: {
    zh_CN: {},
    en_US: {}
  },
  datetimeFormats: {},
  numberFormats: {},
  legacy: false
}

export const useLocale = defineStore(CORE_LOCALE.toString(), {
  state: () => ({
    locale: "zh_CN",
    antdLocaleResource: zh_CN
  }),
  getters: {},
  actions: {
    setLocale(locale: Locale) {
      this.locale = locale
    }
  }
})

/**
 *  统一处理所有涉及国际化的切换
 */
export class DefaultLocaleService implements LocaleService {
  currentLocale(): Ref<Locale> {
    const store = useLocale()
    const { locale } = storeToRefs(store)
    return locale
  }

  currentAntdLocaleResource(): Ref<AntdLocale> {
    const store = useLocale()
    const { antdLocaleResource } = storeToRefs(store)
    return antdLocaleResource
  }

  setLocale(locale: string): void {
    const store = useLocale()

    store.locale = locale
    // vue-i18n
    // this.i18n.locale.value = locale;
    // antd
    store.antdLocaleResource = AntdMap[locale] || zh_CN
    // dayjs
    dayjs.locale(DayJSMap[locale] || "zh-cn")
  }

  mergeLocaleMessage(message: LocaleMessage): void {
    const locales = Object.keys(message)
    // locales.forEach((l) => {
    //   if (l === "zh_CN" || l === "en_US") {
    //     this.i18n.mergeLocaleMessage(l, message[l] || {});
    //   }
    // });
  }
}

/**
 * 包裹vue-i18n，并暴露Composer作为服务，各个插件将自己的国际化内容merge进来
 */
@singleton()
@registry([
  {
    token: CORE_INITIALIZER,
    useFactory: instanceCachingFactory<Initializer>((c) => c.resolve(LocaleInitService))
  }
])
export class LocaleInitService implements Initializer {
  // eslint-disable-next-line @typescript-eslint/ban-types
  i18n: I18n<I18nOptions, {}, {}, false>
  options: LocalOptions

  constructor(
    @injectOptional(LocalOptionsInjectKey)
    options: LocalOptions
  ) {

    const mergedOptions: LocalOptions = {
      ...i18nDefaultOptions,
      ...options
    }

    const userLang = navigator.language.toLowerCase()
    mergedOptions.locale = userLang.includes('zh') ? 'zh_CN' : 'en_US'
    this.i18n = createI18n(mergedOptions)
    this.options = options
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  start(app: App<Element>): void {
    const LocaleService = new DefaultLocaleService()
    LocaleService.setLocale(this.options.locale)
    container.register<LocaleService>(CORE_LOCALE, { useValue: new DefaultLocaleService() })
  }

  started(app: App<Element>): void {
    app.use(this.i18n)
  }
}
